import React, { useMemo } from 'react';
import { AuthenticationClient } from './client';
import { AuthenticationContext, LoginOptions, User } from './types';

const authenticationContext = React.createContext<AuthenticationContext>(null);

const login = (loginOptions?: LoginOptions) => {
  AuthenticationClient.getInstance().login(loginOptions);
};

const logout = () => {
  AuthenticationClient.getInstance().logout();
};

const getAccessToken = (ignoreCache?: boolean): Promise<string> => {
  return AuthenticationClient.getInstance().getAccessToken(ignoreCache);
};

type Props = {
  isGuard?: boolean;
  children: React.ReactNode;
};

function AuthenticationProvider({ children, isGuard = false }: Props) {
  const [isAuthenticated, setAuthenticated] = React.useState<boolean>(false);
  const [isAuthenticating, setAuthenticating] = React.useState<boolean>(false);
  const [user, setUser] = React.useState<User | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    AuthenticationClient.getInstance().subscribe(listener);

    return () => AuthenticationClient.getInstance().unsubscribe(listener);
  }, []);

  const listener = (
    state: [isAuthenticated: boolean, isAuthenticating: boolean, user: User | null, error: Error | null]
  ): void => {
    const [authenticated, authenticating, user, error] = state;
    setUser(user);
    setAuthenticated(authenticated);
    setAuthenticating(authenticating);
    setError(error);
  };

  const value = useMemo(
    () => ({ getAccessToken, login, logout, isAuthenticated, isAuthenticating, user, error }),
    [isAuthenticated, isAuthenticating, user, error]
  );

  return (
    <authenticationContext.Provider value={value}>
      {!isGuard || isAuthenticated ? children : null}
    </authenticationContext.Provider>
  );
}

export { AuthenticationProvider, authenticationContext };
